import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { PERMISSIONS, type CommunicationTemplateSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useTemplateActivationMutation, useTemplatesQuery } from '../api/crm.api';
import { TemplateEditor } from '../components/TemplateEditor';

/**
 * The wording a shop sends, and who may change it.
 *
 * The one idea this page has to get across is that editing a template changes what happens *next*.
 * Messages already prepared carry their own frozen copy, so nobody can rewrite what a customer was
 * told last week by fixing a typo today — which is both the honest behaviour and the one people
 * assume is not true unless it is said out loud.
 */
export function TemplatesPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.NOTIFICATIONS_TEMPLATES_MANAGE);

  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = useTemplatesQuery();
  const activation = useTemplateActivationMutation();

  const toggle = (template: CommunicationTemplateSummary) => {
    setFailure(null);
    activation.mutate(
      { id: template.id, active: !template.isActive, version: template.version },
      {
        onError: (mutationError) =>
          setFailure(apiErrorMessage(mutationError) ?? t('crm.templates.actionFailed')),
      },
    );
  };

  const columns: Column<CommunicationTemplateSummary>[] = [
    {
      key: 'name',
      header: t('crm.fields.name'),
      primary: true,
      render: (row) => <span dir="auto">{row.name}</span>,
    },
    {
      key: 'code',
      header: t('crm.fields.code'),
      render: (row) => <span dir="ltr">{row.code}</span>,
    },
    {
      key: 'purpose',
      header: t('crm.fields.purpose'),
      render: (row) => t(`crm.purpose.${row.purpose}`),
    },
    {
      key: 'channel',
      header: t('crm.fields.channel'),
      hideOnCard: true,
      render: (row) => t(`crm.channel.${row.channel}`),
    },
    {
      key: 'active',
      header: t('crm.fields.status'),
      render: (row) => (
        <Badge tone={row.isActive ? 'success' : 'neutral'}>
          {t(row.isActive ? 'crm.templates.active' : 'crm.templates.inactive')}
        </Badge>
      ),
    },
    {
      key: 'version',
      header: t('crm.fields.version'),
      hideOnCard: true,
      render: (row) => <span dir="ltr">{row.version}</span>,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) =>
        canManage ? (
          <span className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing({ id: row.id })}>
              {t('common.edit')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => toggle(row)}>
              {t(row.isActive ? 'crm.templates.deactivate' : 'crm.templates.reactivate')}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('crm.templates.title')}
        subtitle={t('crm.templates.subtitle')}
        actions={
          canManage ? (
            <Button onClick={() => setEditing({ id: null })}>{t('crm.templates.create')}</Button>
          ) : undefined
        }
      />

      {failure ? <Alert tone="error">{failure}</Alert> : null}

      <Alert tone="info">{t('crm.templates.versionNote')}</Alert>

      <Card>
        <CardHeader
          title={t('crm.templates.listTitle')}
          subtitle={
            data ? t('crm.templates.count', { count: data.pagination.totalItems }) : undefined
          }
        />
        <DataTable
          caption={t('crm.templates.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(row) => row.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('crm.templates.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={FileText}
          emptyTitle={t('crm.templates.empty')}
          emptyDescription={t('crm.templates.emptyHint')}
        />
      </Card>

      {editing ? <TemplateEditor templateId={editing.id} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
